import { Component, OnDestroy, ViewChild } from '@angular/core';
import { asyncScheduler, filter, map, Observable, observeOn, Subscription, switchMap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Briefcase } from '../../../models/ui/briefcase.model';
import { BriefcaseManager } from '../../../managers/briefcase.manager';
import { DownloadBriefcaseDialogComponent } from './download-briefcase-dialog/download-briefcase-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { HelpService } from '../../../services/help.service';
import { NotificationHubSdk } from 'src/app/sdks/notification.hub-sdk';
import { Store } from "@ngrx/store";
import { selectIsIndexingAndIsProcessing } from "@store/global/global.selectors";

@Component({
    selector: 'lex-download-briefcase',
    templateUrl: './download-briefcase.component.html',
    styleUrls: ['./download-briefcase.component.scss'],
    standalone: false
})
export class DownloadBriefcaseComponent implements OnDestroy {
	public totalEntries$: Observable<number>;
	public hasPreviousPage$: Observable<boolean>;
	public hasNextPage$: Observable<boolean>;
	public dataSource$: Observable<MatTableDataSource<Briefcase>>;
	private subscription: Subscription = new Subscription();
	private skip: number = 0;
	private take: number = 200;

	public columnsToDisplay = [
		'id',
		'title',
		'createdAt',
		'customDesignation',
		'createdByEmail',
		'batesStampPages',
		'totalDocuments',
		'size',
		'remove'
	];
	private refreshTimer: number;

	@ViewChild('paginator', { static: false }) paginator: MatPaginator;

	constructor(private briefcaseManager: BriefcaseManager, private matDialog: MatDialog, private notificationHubSdk: NotificationHubSdk, private helpService: HelpService, private store: Store) {
		this.dataSource$ = this.briefcaseManager.briefcaseCollection$.pipe(
			filter((collection) => !!collection),
			map((logCollection) => new MatTableDataSource(logCollection?.items))
		);

		this.totalEntries$ = this.briefcaseManager.briefcaseCollection$.pipe(
			map((collection) => collection?.totalItems || 0)
		);

		this.hasNextPage$ = this.totalEntries$.pipe(
			observeOn(asyncScheduler),
			map(() => this.paginator?.hasNextPage())
		);

		this.hasPreviousPage$ = this.totalEntries$.pipe(
			observeOn(asyncScheduler),
			map(() => this.paginator?.hasPreviousPage())
		);
		this.briefcaseManager.refreshBriefcases({ skip: this.skip, take: this.take });

		this.scheduleBriefcaseRefresh();
		this.subscription = this.store.select(selectIsIndexingAndIsProcessing).subscribe(() => this.briefcaseManager.refreshBriefcases({skip: this.skip, take: this.take}));
		this.helpService.updateHelpLink("case-menu/download-documents");
	}

	ngOnDestroy(): void {
		clearTimeout(this.refreshTimer);
		this.subscription.unsubscribe();
	}

	public loadPreviousPage(): void {
		this.paginator?.previousPage();
	}

	public loadNextPage(): void {
		this.paginator?.nextPage();
	}

	public delete(id: string): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Briefcase`,
			message: `This briefcase will be <strong>PERMANENTLY DELETED</strong>.
          <br/> This action cannot be undone.`
		};

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked: boolean) => {
					if (okClicked) {
						return this.briefcaseManager.removeBriefcase(id);
					}
				})
			).subscribe(() => {
			this.briefcaseManager.refreshBriefcases({ skip: this.skip, take: this.take });
		});
	}

	public scheduleBriefcaseRefresh(): void {
		this.refreshTimer = setTimeout(() => {
			this.briefcaseManager.refreshBriefcases({ skip: this.skip, take: this.take });
			this.scheduleBriefcaseRefresh();
		}, 30000);
	}

	public movePage(event): void {
		this.skip = event.pageIndex * event.pageSize;
		this.take = event.pageSize;
		this.briefcaseManager.refreshBriefcases({ skip: this.skip, take: this.take });
	}

	public download(briefcase: Briefcase): void {
		this.briefcaseManager.getFileList(briefcase.id).subscribe((response) => {
			const dialogConfig = new MatDialogConfig();
			dialogConfig.autoFocus = true;
			dialogConfig.width = '500px';
			dialogConfig.data = {
				files: response,
				briefcase: briefcase
			};
			const dialogRef = this.matDialog.open(DownloadBriefcaseDialogComponent, dialogConfig);
			dialogRef.afterClosed().subscribe(() => this.briefcaseManager.refreshBriefcases({
				skip: this.skip,
				take: this.take
			}));
		});
	}
}
